import { Deferred, Head, Link, router, usePage } from "@inertiajs/react"
import { Plus, Upload } from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { ListShell } from "@/components/admin/list-shell"
import { ActiveFilterChips } from "@/components/candidates/active-filter-chips"
import { AddCandidateSheet } from "@/components/candidates/add-candidate-sheet"
import { BulkRejectConfirmation } from "@/components/candidates/bulk-reject-confirmation"
import { CandidateActiveRemovedToggle } from "@/components/candidates/candidate-active-removed-toggle"
import { CandidateBulkActionBar } from "@/components/candidates/candidate-bulk-action-bar"
import { CandidateEmptyState } from "@/components/candidates/candidate-empty-state"
import { CandidateFilterRail } from "@/components/candidates/candidate-filter-rail"
import { CandidateNoResultsPanel } from "@/components/candidates/candidate-no-results-panel"
import { CandidatePaginationFooter } from "@/components/candidates/candidate-pagination-footer"
import {
  CandidateAllRemovedEmptyState,
  CandidateRemovedViewEmptyState,
} from "@/components/candidates/candidate-removed-empty-states"
import { CandidateSearchInput } from "@/components/candidates/candidate-search-input"
import { CandidateStatusTabs } from "@/components/candidates/candidate-status-tabs"
import { CandidateTable } from "@/components/candidates/candidate-table"
import {
  InviteActivityChart,
  InviteActivityChartSkeleton,
} from "@/components/funnel/invite-activity-chart"
import {
  InviteFunnelMetrics,
  InviteFunnelMetricsSkeleton,
} from "@/components/funnel/invite-funnel-metrics"
import { InviteFunnelRangeFilter } from "@/components/funnel/invite-funnel-range-filter"
import {
  StageTimeTable,
  StageTimeTableSkeleton,
} from "@/components/funnel/stage-time-table"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useBulkSelection } from "@/hooks/use-bulk-selection"
import { useCandidateListUrl } from "@/hooks/use-candidate-list-url"
import { useCurrentContext } from "@/hooks/use-current-context"
import AppLayout from "@/layouts/app-layout"
import { finalFocusByTestId } from "@/lib/final-focus"
import { candidatesPath, importCandidatesPath } from "@/routes"
import type { FlashData, FlashErrorObject } from "@/types"
import type {
  BulkResult,
  CandidatesIndexProps,
  FlashSuccessObject,
} from "@/types/candidates"
import type { FunnelData, FunnelRangePreset } from "@/types/invites"

/**
 * Map a `FunnelData` to a localised range label for the
 * `<InviteFunnelMetrics>` `Generated` card sub-line. Returns the
 * preset label ("Last 7 days" / "Last 30 days") for the canonical
 * presets, or a "May 1 – May 30" fallback for custom ranges.
 *
 * Calling order matters: keep this in sync with `resolveFunnelPreset`.
 */
function resolveRangeLabel(
  t: ReturnType<typeof useTranslation>["t"],
  funnel: FunnelData,
): string {
  const preset = resolveFunnelPreset(funnel)
  if (preset === "7d") return t("invites.funnel.range.last_7d")
  if (preset === "30d") return t("invites.funnel.range.last_30d")
  // Custom range → "Mon D – Mon D" using locale-aware short format.
  const start = new Date(funnel.range_start).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  })
  const end = new Date(funnel.range_end).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  })
  return `${start} – ${end}`
}

/**
 * Map a `FunnelData` (from BE) to a `FunnelRangePreset` so the
 * `<InviteFunnelRangeFilter>` can highlight the active preset.
 *
 * Heuristic: compare the `to` date to today (in browser TZ — close
 * enough; BE-side is brand-TZ which may diverge by hours, but the
 * preset highlight is informational).
 *   - 7-day window AND `to === today` → `"7d"`
 *   - 30-day window AND `to === today` → `"30d"`
 *   - anything else → `"custom"`
 */
function resolveFunnelPreset(funnel: FunnelData): FunnelRangePreset {
  const today = new Date()
  const todayIso = `${today.getFullYear()}-${String(
    today.getMonth() + 1,
  ).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`
  if (funnel.range_end !== todayIso) return "custom"

  const fromDate = new Date(funnel.range_start)
  const toDate = new Date(funnel.range_end)
  const days = Math.round(
    (toDate.getTime() - fromDate.getTime()) / (1000 * 60 * 60 * 24),
  )
  if (days === 6) return "7d"
  if (days === 29) return "30d"
  return "custom"
}

/**
 * pages/Brand/Candidates/Index — OKS-17 Candidate List.
 *
 * Replaces OKS-16's placeholder list. Surfaces the full URL-driven
 * filter/search/sort/paginate experience plus the single-add Sheet
 * (OKS-629, URL-driven via `?add=1`) and bulk-CSV link entrypoints.
 *
 * **URL is the single source of truth** (UX Spec + FE TRD): every
 * filter/sort/page mutation rewrites the URL via `useCandidateListUrl`
 * which emits Inertia partial reloads scoped to the data props
 * (keeps brand chip + sidebar mounted; page-shell never unmounts).
 *
 * **5 states** (UX Spec):
 *   - Loading — `CandidateListSkeleton` (during partial reload)
 *   - Empty — `CandidateEmptyState` when `is_empty: true`
 *   - No-results — `CandidateNoResultsPanel` when `!is_empty &&
 *     candidates.length === 0`
 *   - Error — Inertia visit failure → handled by ErrorBoundary upstream;
 *     shell-level offline banner is wired via `useOnline` inside
 *     `ListShell`
 *   - Offline — sticky banner above header (ListShell auto-renders)
 *
 * **Status pipeline tabs:** all 9 enum values + "All" rendered always
 * with `(0)` counts on empty buckets — clickable always (CTPO lock).
 *
 * **Locked QA testid contract** (28 testids, transcribed from UX Spec):
 *   `candidate-list-page` · `candidate-list-search` ·
 *   `candidate-list-status-tabs` · `candidate-list-status-tab-{status}` ·
 *   `candidate-list-filter-{button|followers|country|city|tags|clear}` ·
 *   `candidate-list-filter-followers-{min|max|apply}` ·
 *   `candidate-list-sort` · `candidate-list-table` ·
 *   `candidate-list-row` · `candidate-list-row-{id}` ·
 *   `candidate-list-row-status-{id}` · `candidate-list-pagination` ·
 *   `candidate-list-pagination-{prev|next}` ·
 *   `candidate-list-empty` · `candidate-list-empty-cta` ·
 *   `candidate-list-no-results` · `candidate-list-no-results-clear` ·
 *   `candidate-list-loading` · `candidate-list-error` ·
 *   `candidate-list-error-retry`.
 *
 * **Error boundary (Phase 4 F1 fix):** `router.on('error', …)` flips
 * `hasError` → `<Alert>` swaps in for the table region with a
 * `Retry` button that calls `router.reload()`. `router.on('start',
 * …)` clears the flag on the next visit. Mirrors the OKS-18
 * cross-brand error boundary pattern.
 *
 * **Add-flow continuity:** `add-candidate-trigger` (split-button menu)
 * + `bulk-import-link` live on the list; `add-candidate-sheet` (OKS-629
 * rename from `add-candidate-dialog`), `add-candidate-form` live in the
 * URL-driven Sheet mounted at page level (different surface from the
 * list itself). The single-add menu item navigates to `?add=1`.
 */
export default function CandidatesIndex({
  candidates,
  filters,
  sort,
  pagination,
  filter_options,
  is_empty,
  bulk,
  funnel,
  stage_analytics: stageAnalytics,
  just_added_id,
  removed_view,
  removed_count,
  view_is_empty,
  show_add,
}: CandidatesIndexProps) {
  const { t } = useTranslation()
  const { currentBrand } = useCurrentContext()
  const brandName = currentBrand?.name ?? ""
  const list = useCandidateListUrl(filters, sort)
  const { url } = usePage()

  // ── OKS-19 — bulk selection plumbing ────────────────────────────
  const selection = useBulkSelection()
  const [bulkRejectOpen, setBulkRejectOpen] = useState(false)
  const [bulkSubmitting, setBulkSubmitting] = useState(false)

  // Selected ids snapshot for the bulk POST. Materialised inside a
  // memo so the array identity is stable until the Set changes —
  // matters because BulkRejectConfirmation renders one hidden input
  // per id and React would re-key on every render otherwise.
  const selectedIdsArray = useMemo(
    () => Array.from(selection.selected),
    [selection.selected],
  )

  // OKS-952 — distinct statuses across the selection, feeding the bulk reason
  // picker's intersection. Rows not on the current page cannot be selected
  // (selection resets on every Inertia visit), so `candidates` always covers
  // the whole selection.
  const selectedRows = useMemo(
    () =>
      candidates
        .filter((row) => selection.selected.has(row.id))
        .map((row) => ({ id: row.id, status: row.status })),
    [candidates, selection.selected],
  )

  // ── OKS-19 — bulk-skipped URL parsing ────────────────────────────
  // Parse `?bulk_skipped=42,91,12` from the URL. Drives the skipped
  // chip rendering in ActiveFilterChips. Server is authoritative on
  // the actual filter (BE's ListQuery clamps under brand floor); FE
  // just renders the chip + handles removal.
  const bulkSkippedIds = useMemo(() => parseBulkSkipped(url), [url])
  const bulkSkippedCount = bulkSkippedIds.length

  // ── OKS-19 — sonner toast on flash[:success].bulk_result ────────
  // BE 303-redirects to /candidates with a structured flash payload
  // after every bulk submit. The toast renders here (NOT in
  // useFlash — that hook only handles plain-string flash.success;
  // bulk_result is the structured object form per memory
  // feedback_useflash_dropped_silently). Sonner's `action` slot
  // carries the View-skipped link — built FE-side from
  // bulk_result.skipped_ids[].
  const flash = usePage().flash
  const bulkResult = useMemo(() => extractBulkResult(flash), [flash])

  // ── OKS-19 P4 fix #2 — cap_exceeded inline alert wiring ─────────
  // `flash.error` is a discriminated union (`string | FlashErrorObject`).
  // The bulk `cap_exceeded` branch carries `{code: "too_many",
  // message: ...}` per BE Lead's `11f3e5c`. Other error branches
  // stay as plain strings. Read the structured form to drive the
  // CandidateBulkActionBar.capExceeded inline alert (defense-in-
  // depth — V1 has no DOM-reachable trigger; only URL-smuggle of
  // 26+ ids reaches here, but the prop wires it for free).
  const capExceeded = isCapExceededError(flash?.error)
  const lastToastedSignature = useRef<string | null>(null)
  useEffect(() => {
    if (!bulkResult) return
    // Sonner toasts can replay across re-renders if we don't
    // de-duplicate. Build a stable signature per redirect (counts +
    // ids + event); only fire when it changes. Any same-page
    // re-render with identical bulk_result won't re-toast.
    const signature = bulkResultSignature(bulkResult)
    if (lastToastedSignature.current === signature) return
    lastToastedSignature.current = signature
    showBulkResultToast(bulkResult, t)
  }, [bulkResult, t])

  // Phase 4 F1 — page-level error boundary. Listens for Inertia
  // visit errors during partial reloads (filter mutations, pagination)
  // and surfaces the locked `candidate-list-error` + `…-error-retry`
  // testids. Without this, a failed visit would leave the page in
  // a stale state with no recovery affordance.
  const [hasError, setHasError] = useState(false)
  useEffect(() => {
    const offError = router.on("error", () => setHasError(true))
    const offStart = router.on("start", () => setHasError(false))
    return () => {
      offError()
      offStart()
    }
  }, [])

  // ── OKS-849 — reconcile the stranded `?add=1` marker ─────────────
  // Page-scoped URL-reconciliation guard, donor pattern from the
  // Brand/Rewards editor (OKS-780, `20db45ce`). Reconcile the `?add`
  // marker against the LIVE address on every Inertia visit completion.
  //
  // `/candidates` mounts a deferred-props batch — the SHARED
  // `recent_notifications` (application_controller.rb:184, gated
  // `if Current.user && Current.brand`) rides alongside the page-local
  // `funnel` (candidates_controller.rb:213). That batch is born on mount
  // carrying the current `?add=1`. If the user dismisses the Sheet before
  // it lands, the deferred reload applies LAST and pushes its birth URL
  // back: the Sheet stays closed (`show_add` is omitted from the
  // deferred's `only` partial, so it holds the `closeSheet()` set) but the
  // address re-strands `?add=1` — a refresh then reads it server-side
  // (`params[:add].present?`) and reopens the dismissed Sheet. OKS-746
  // mechanism; framework-wide fix is parked in OKS-788.
  //
  // Why this shape (and not a render-keyed `usePage().url` effect):
  //   - The deferred re-strands `window.location` via Inertia's own
  //     history write. An effect keyed on `usePage().url` is BLIND to that
  //     write once the prop-reflected URL and the live address diverge
  //     (the OKS-744 two-URL-truths trap), so it never re-fires and the
  //     strand stands. We read `window.location.search` LIVE at reconcile
  //     time, never a prop snapshot.
  //   - We reconcile on the `finish`/`navigate` events, which fire AFTER a
  //     visit's history write commits (the deferred reload emits them via
  //     `loadDeferredProps → doReload`). Firing after the deferred's own
  //     write means there is no later queued write for `router.replace` to
  //     lose to — the failure mode of the render-keyed attempt.
  //
  // Unlike the Rewards donor (which navigates to a bare `rewardsPath()`),
  // Candidates carries list state, so the reconcile PRESERVES the current
  // list params (filters / sort / `removed` / `country` / `from` / `to` /
  // page) and strips only `add` — a bare nav would drop `?removed=1` /
  // `?country=` and re-trigger the France-default 303. `preserveState`
  // keeps the table mounted; `preserveScroll` holds position.
  //
  // A legitimate open (`show_add === true`) is always a no-op via the ref
  // guard, so the Sheet can never be force-closed; once the marker is gone
  // the reconcile is a no-op, so `router.replace`'s own event re-fire does
  // not loop.
  const showAddRef = useRef(show_add)
  useEffect(() => {
    showAddRef.current = show_add
  }, [show_add])
  useEffect(() => {
    const reconcile = () => {
      if (showAddRef.current) return
      if (!urlHasAddMarker(window.location.search)) return
      const params = currentListParams()
      delete params.add
      router.replace({
        url: candidatesPath(params),
        preserveScroll: true,
        preserveState: true,
      })
    }
    const stopFinish = router.on("finish", reconcile)
    const stopNavigate = router.on("navigate", reconcile)
    // Catch a strand already present when the effect mounts.
    reconcile()
    return () => {
      stopFinish()
      stopNavigate()
    }
  }, [])

  // ── OKS-127 — URL scrub for the recency-highlight marker ───────
  // Strip `?just_added=<id>` once on mount so back-button / refresh
  // / shared-link copies do not re-trigger the row highlight. Runs
  // unconditionally when the prop is non-null — independent of
  // whether the matching row is in the current view (filter /
  // pagination miss is a silent no-op per UX Spec §1.Edge cases,
  // but the marker still has to be cleared from the URL).
  //
  // `history.replaceState` is used (NOT a router visit) so the
  // Inertia page tree is preserved — no re-fetch, no flicker. All
  // other query string keys (filters, sort, page, country, q,
  // bulk_skipped, etc.) are preserved verbatim. Extracted into
  // `buildScrubbedJustAddedUrl` for direct unit-testability.
  useEffect(() => {
    if (just_added_id == null) return
    const scrubbed = buildScrubbedJustAddedUrl(window.location.search)
    window.history.replaceState({}, "", scrubbed)
    // Intentionally fire once on mount only. Re-running on
    // `just_added_id` changes would race with the row's own fade
    // lifecycle; the BE only ever paints the marker via redirect,
    // so a same-page-prop update is impossible in practice anyway.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── OKS-465 — view-aware state selection ─────────────────────────
  // `removed_view` flips the whole surface between the active pipeline
  // and the Removed view. State precedence per UX Spec §3:
  //   active view:
  //     - is_empty (zero candidates ever)        → add-first CTA
  //     - !is_empty && view_is_empty (all removed) → "No active / View removed (N)"
  //     - !is_empty && !view_is_empty && 0 rows  → clear-filters no-results
  //     - rows                                   → table
  //   removed view:
  //     - view_is_empty (nothing removed)        → "You haven't removed anyone"
  //     - view_is_empty false && 0 rows          → clear-filters no-results
  //     - rows                                   → table (with Restore actions)
  const noRows = candidates.length === 0
  const showError = hasError
  const showEmpty = !showError && !removed_view && is_empty
  const showAllRemoved =
    !showError && !removed_view && !is_empty && view_is_empty
  const showRemovedEmpty = !showError && removed_view && view_is_empty
  const showNoResults =
    !showError && !showEmpty && !showAllRemoved && !showRemovedEmpty && noRows
  const showTable = !showError && !noRows

  // ── OKS-19 — bulk submit handlers ────────────────────────────────
  function submitBulkApprove() {
    // Approve has no modal — direct programmatic POST. Wire format
    // matches BE's locked nested strong-params signature
    // `params.require(:bulk_transition).permit(:event, :reason,
    // candidate_ids: [])`. Vitest covers the call payload shape
    // separately from BulkRejectConfirmation's literal-`name=`
    // canaries (since this path is programmatic not form-based).
    if (selection.size === 0 || bulkSubmitting) return
    setBulkSubmitting(true)
    router.post(
      bulk.transition_url,
      {
        bulk_transition: {
          event: "approve",
          candidate_ids: selectedIdsArray,
        },
      },
      {
        preserveScroll: true,
        onFinish: () => setBulkSubmitting(false),
      },
    )
  }

  /**
   * OKS-950 — bulk validate. Same programmatic shape as `submitBulkApprove`,
   * different event. The server's `SUPPORTED_EVENTS` plus the per-row
   * `TransitionPermission` composer are what actually gate this, so the
   * button's presence is convenience and never the security boundary.
   *
   * No confirmation, for parity with bulk approve: the reachable batch is one
   * page of 25 (PRD OQ2), and every row moves forward to a decision the brand
   * manager just made deliberately.
   */
  function submitBulkValidate() {
    if (selection.size === 0 || bulkSubmitting) return
    setBulkSubmitting(true)
    router.post(
      bulk.transition_url,
      {
        bulk_transition: {
          event: "validate",
          candidate_ids: selectedIdsArray,
        },
      },
      {
        preserveScroll: true,
        onFinish: () => setBulkSubmitting(false),
      },
    )
  }

  function handleBulkRejectClick() {
    if (selection.size === 0 || bulkSubmitting) return
    setBulkRejectOpen(true)
  }

  function handleViewSkipped() {
    // Sonner action button click → navigate to the filtered subset.
    // Same href the toast's action link would carry; here we route
    // through `router.get` so back-button works cleanly.
    if (bulkResult?.skipped_ids.length) {
      router.get(
        candidatesPath({ bulk_skipped: bulkResult.skipped_ids.join(",") }),
        {},
        { preserveState: true, preserveScroll: true },
      )
    }
  }

  function handleClearBulkSkipped() {
    // Phase 4 P2 fix — clear ONLY the `?bulk_skipped=` param while
    // preserving every other active URL filter (status, country,
    // city, tags, q, sort, page). Original implementation navigated
    // to bare `candidatesPath()` which silently discarded user's
    // active filter state — confusing UX after a bulk action.
    //
    // URL-building extracted into `buildClearBulkSkippedUrl` for
    // direct unit-testability against the canonical preserve-vs-
    // drop matrix.
    router.get(
      buildClearBulkSkippedUrl(window.location.search),
      {},
      { preserveState: true, preserveScroll: true },
    )
  }

  function handleRemoveFilter(key: keyof typeof filters, value?: string) {
    if (key === "status" && value) {
      list.setFilter(
        "status",
        filters.status.filter((s) => s !== value),
      )
    } else if (key === "tags" && value) {
      list.setFilter(
        "tags",
        filters.tags.filter((t) => t !== value),
      )
    } else if (key === "followers_min" || key === "followers_max") {
      // Both followers params travel together — clear both at once.
      list.push({ followers_min: null, followers_max: null }, undefined, 1)
    } else if (key === "country") {
      // CTPO V1 lock: removing the country chip writes "" (explicit
      // opt-out of default-France), NOT null. `null` would re-trigger
      // the default on the next visit; "" preserves "user cleared"
      // intent in the URL.
      list.setFilter("country", "")
    } else if (key === "city") {
      list.setFilter("city", null)
    } else if (key === "q") {
      list.setFilter("q", null)
    }
  }

  return (
    <AppLayout>
      <Head title={t("candidates.list.head_title")} />
      <div data-testid="candidate-list-page">
        {/*
          OKS-21 v2 — Recruitment funnel analytics header strip.
          Renders atop the candidates list per UX Spec addendum §2.2 v2.
          Replaces the v1 4-step horizontal funnel + collapsible day
          table (rejected by CTPO during Phase 3 review):
            - `<InviteFunnelMetrics>` — 4 shadcn stat tiles (mobile
              2×2 / desktop 4-across) with conversion %s in each
              card's sub-line
            - `<InviteActivityChart>` — recharts line chart, 4 series,
              daily counts, sr-only `<table>` mirror for SR users +
              Playwright stable selectors
            - `<InviteFunnelRangeFilter>` — unchanged, drives both via
              `router.reload({ only: ["funnel"], data: { from, to } })`
        */}
        <div className="mb-6 flex flex-col gap-4 px-4 pt-4 sm:px-6 sm:pt-6">
          <Deferred data="funnel" fallback={<InviteFunnelMetricsSkeleton />}>
            {funnel ? (
              <>
                <div className="flex items-start justify-end gap-3">
                  <InviteFunnelRangeFilter
                    value={resolveFunnelPreset(funnel)}
                    initialFrom={funnel.range_start}
                    initialTo={funnel.range_end}
                  />
                </div>
                <InviteFunnelMetrics
                  funnel={funnel}
                  rangeLabel={resolveRangeLabel(t, funnel)}
                />
              </>
            ) : (
              <InviteFunnelMetricsSkeleton />
            )}
          </Deferred>
          <Deferred data="funnel" fallback={<InviteActivityChartSkeleton />}>
            {funnel ? (
              <InviteActivityChart funnel={funnel} />
            ) : (
              <InviteActivityChartSkeleton />
            )}
          </Deferred>
          {/*
            OKS-956 — time-in-stage p50/p90. A SEPARATE deferred key from
            `funnel`: the range filter above reloads `only: ["funnel"]`, and
            these percentiles have no range to scope, so they must not be
            recomputed on every date tap. Both keys carry the default defer
            group, so they still resolve in one follow-up request.

            `stageAnalytics == null` after resolve means the funnel gate
            refused this viewer — render NOTHING, not an error panel. Showing
            someone a broken-looking retry box for a widget they are not
            entitled to misreports a permission as a fault. (Note this differs
            from the `funnel` branches above, which fall back to a skeleton on
            null and so leave a refused viewer looking at a permanent loading
            state — a pre-existing quirk we deliberately do not copy.)
          */}
          <Deferred
            data="stage_analytics"
            fallback={<StageTimeTableSkeleton />}
          >
            {stageAnalytics ? (
              <StageTimeTable
                section={stageAnalytics}
                onRetry={() => router.reload({ only: ["stage_analytics"] })}
              />
            ) : null}
          </Deferred>
        </div>
        <ListShell
          testId="candidate-list-shell"
          title={t("candidates.list.page_title")}
          subtitle={
            brandName ? (
              <span className="inline-flex items-center gap-2">
                <Badge
                  variant="secondary"
                  data-testid="candidate-list-brand-chip"
                  aria-label={t("candidates.list.brand_chip_aria", {
                    brand: brandName,
                  })}
                >
                  {brandName}
                </Badge>
              </span>
            ) : undefined
          }
          action={<AddCandidateMenu />}
          toolbar={
            !showEmpty && (
              <div className="flex flex-col gap-4">
                {/*
                  OKS-465 — Active⇄Removed view switch. Shown above the
                  status tabs. In the Removed view the status tabs are
                  suppressed (removal is orthogonal to status; status
                  filtering doesn't apply to the removed roster — UX
                  Spec §2).
                */}
                <CandidateActiveRemovedToggle
                  removedView={removed_view}
                  removedCount={removed_count}
                />
                {!removed_view && (
                  <CandidateStatusTabs
                    filters={filters}
                    filterOptions={filter_options}
                    onSelect={list.setStatusOnly}
                  />
                )}
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <CandidateFilterRail
                    filters={filters}
                    sort={sort}
                    filterOptions={filter_options}
                    onFollowersChange={(min, max) =>
                      list.push(
                        { followers_min: min, followers_max: max },
                        undefined,
                        1,
                      )
                    }
                    onCountryChange={(c) => list.setFilter("country", c)}
                    onCityChange={(c) => list.setFilter("city", c)}
                    onTagsChange={(tags) => list.setFilter("tags", tags)}
                    onReasonCodeChange={(codes) =>
                      list.setFilter("reason_code", codes)
                    }
                    onContactChannelChange={(channels) =>
                      list.setFilter("contact_channel", channels)
                    }
                    onSortChange={list.setSort}
                    onClearAll={list.clearAll}
                  />
                  <CandidateSearchInput
                    defaultValue={filters.q ?? ""}
                    onDebouncedChange={(q) => list.setFilter("q", q || null)}
                    className="sm:max-w-xs sm:flex-shrink-0"
                  />
                </div>
              </div>
            )
          }
          footer={
            showTable && (
              <CandidatePaginationFooter
                pagination={pagination}
                perPage={25}
                onPageChange={list.setPage}
              />
            )
          }
        >
          {showError && (
            <Alert
              variant="destructive"
              data-testid="candidate-list-error"
              role="alert"
              className="flex flex-col gap-3 sm:flex-row sm:items-start"
            >
              <div className="flex-1">
                <AlertTitle>{t("candidates.list.error.title")}</AlertTitle>
                <AlertDescription>
                  {t("candidates.list.error.description")}
                </AlertDescription>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                data-testid="candidate-list-error-retry"
                onClick={() => {
                  setHasError(false)
                  router.reload()
                }}
              >
                {t("candidates.list.error.retry")}
              </Button>
            </Alert>
          )}
          {showEmpty && <CandidateEmptyState cta={<AddCandidateMenu />} />}
          {/* OKS-465 — active view, all candidates removed. */}
          {showAllRemoved && (
            <CandidateAllRemovedEmptyState removedCount={removed_count} />
          )}
          {/* OKS-465 — Removed view, nothing removed. */}
          {showRemovedEmpty && <CandidateRemovedViewEmptyState />}
          {showNoResults && (
            <CandidateNoResultsPanel
              filters={filters}
              onClearAll={list.clearAll}
              onRemoveFilter={handleRemoveFilter}
            />
          )}
          {showTable && (
            <>
              {/*
                OKS-19 — bulk-skipped chip rail. Renders above the
                table whenever `?bulk_skipped=` is present in the URL.
                Shows a single "Skipped from last bulk action (N)"
                chip; clicking the X navigates to /candidates without
                the param, returning to the broader view.
              */}
              {/* Bulk-skipped chips + bulk selection do not apply in the
                  Removed view (status transitions are suppressed there). */}
              {!removed_view && bulkSkippedCount > 0 && (
                <div className="mb-3">
                  <ActiveFilterChips
                    filters={filters}
                    onRemove={handleRemoveFilter}
                    bulkSkippedCount={bulkSkippedCount}
                    onBulkSkippedRemove={handleClearBulkSkipped}
                  />
                </div>
              )}
              <CandidateTable
                rows={candidates}
                sort={sort}
                selection={removed_view ? undefined : selection}
                justAddedId={just_added_id}
                removedView={removed_view}
              />
            </>
          )}
        </ListShell>
      </div>
      {/*
        OKS-19 — sticky-bottom bulk action bar (F4). Mounted outside
        the ListShell so its `position: fixed` doesn't fight the
        scroll container. Internal mount gate handles selectedCount===0.
        OKS-465 — suppressed entirely in the Removed view (bulk status
        transitions don't apply to removed candidates).
      */}
      {!removed_view && (
        <CandidateBulkActionBar
          selectedCount={selection.size}
          onClear={selection.clear}
          onApprove={submitBulkApprove}
          onValidate={submitBulkValidate}
          onReject={handleBulkRejectClick}
          // OKS-950 — the server decides WHETHER a verb is available;
          // `activeStatus` decides WHICH forward verb is meaningful here.
          // Only a SINGLE selected status tab pins a verb: on "all" or a
          // multi-status filter the rows are mixed, so the tab tells us
          // nothing and we fall back to Approve.
          permittedEvents={bulk.events}
          activeStatus={filters.status.length === 1 ? filters.status[0] : null}
          submitting={bulkSubmitting}
          capExceeded={capExceeded}
        />
      )}
      {/*
        OKS-19 — bulk reject confirmation modal (F5). Controlled
        open state; opens when the bar's Reject is clicked. Snapshots
        the current selection at open-time so a parallel selection
        change can't desync the form's hidden-input array. (selection
        clears after the redirect anyway via useBulkSelection's
        router.on("start") subscription.)
      */}
      {!removed_view && (
        <BulkRejectConfirmation
          open={bulkRejectOpen}
          onOpenChange={setBulkRejectOpen}
          actionUrl={bulk.transition_url}
          selectedIds={selectedIdsArray}
          // OKS-952 — the SELECTED rows (id + status), so the dialog can narrow
          // the reason list to codes legal for every one of them and partition
          // them for §3.4's one-click narrowing. Read from the rows already on
          // the page; the bulk bar itself only knows the count and the active
          // tab, and a round-trip would be absurd for data we are rendering.
          selectedRows={selectedRows}
          // 🔴 `retain`, never `forEach(toggle)`: toggle on an unselected id
          // ADDS it, so a toggle-based narrowing silently WIDENS the batch and
          // would reject candidates the user never picked. Remove-only is a
          // product invariant (§3.4), not an implementation preference.
          onNarrow={(ids) => selection.retain(ids)}
          onSuccess={() => setBulkRejectOpen(false)}
        />
      )}
      {/*
        OKS-19 — sonner action button for the bulk-result toast.
        The toast itself is dispatched in the useEffect above; the
        action button needs a router-coupled handler that lives in
        the component closure. Sonner doesn't expose the action
        binding from a top-level toast() call cleanly; rendered
        action button via toast.success(..., { action: { ... } })
        in showBulkResultToast pulls the navigate fn off this
        ref. See showBulkResultToast comment.
      */}
      <ViewSkippedHandlerSlot onClick={handleViewSkipped} />

      {/*
        OKS-629 — add-candidate Sheet. Mounted once at page level, its
        open state driven by the `?add=1` URL param (`show_add`),
        mirroring the RewardSheet pattern. Close clears the param via
        `closeSheet()`; a successful create redirects to the clean list
        URL (server drops `?add`), flipping `show_add` to false.
      */}
      <AddCandidateSheet
        open={show_add}
        onOpenChange={(next) => {
          if (!next) closeSheet()
        }}
        brandName={brandName}
        finalFocus={finalFocusByTestId("add-candidate-trigger")}
      />
    </AppLayout>
  )
}

/**
 * OKS-629 — URL-state open/close helpers for the add-candidate Sheet,
 * modelled on `Brand/Rewards/Index.tsx`'s `openSheet`/`closeSheet` but
 * adapted for the candidate list's richer URL state.
 *
 * Open sets `?add=1`; close clears it. Both are partial navigations
 * (`preserveScroll` + `preserveState`, reloading only the `show_add`
 * prop) so the list below the scrim never reloads or flashes.
 *
 * Unlike rewards, the candidate list carries filter/sort/view params
 * AND a server-side default-France canonicalization redirect (bare
 * `/candidates` → `?country=France`). So — unlike the rewards helper —
 * we MERGE `add` into the CURRENT query params rather than navigating
 * to a bare path: dropping the existing filters would both lose the
 * user's list state and (on close/open) re-trigger the France 303.
 * The base path still comes from the `candidatesPath` js-routes helper;
 * only the query object is composed here.
 *
 * The single-add menu item calls `openSheet()`; the Sheet's dismissal
 * paths (Escape / outside click / Cancel) call `closeSheet()`. A
 * successful create is closed by the server redirect (drops `?add`),
 * not these helpers.
 *
 * OKS-662 — These helpers deliberately do NOT touch the Inertia page error
 * bag. The Sheet renders its inline errors from `<Form>`'s own render-prop
 * errors, which survive the `redirect_back`; the bag is not a render source
 * anywhere in the app. See `docs/frontend/inertia-error-bag.md`.
 */
function currentListParams(): Record<string, string> {
  return Object.fromEntries(new URLSearchParams(window.location.search))
}

/**
 * OKS-849 — true when `search` carries an `add` marker that would reopen
 * the Sheet on a fresh server read (`?add=<non-blank>`).
 *
 * Mirrors the controller's `params[:add].present?` semantics
 * (`show_add`, candidates_controller.rb:161): a param that is absent, or
 * present-but-blank/whitespace, does NOT open the Sheet, so it is not a
 * strand and is left untouched. `search` is a `window.location.search`
 * string (leading `?` optional). Exported for the OKS-849 reconcile Vitest.
 */
export function urlHasAddMarker(search: string): boolean {
  const query = search.startsWith("?") ? search.slice(1) : search
  const value = new URLSearchParams(query).get("add")
  return value !== null && value.trim() !== ""
}

function openSheet() {
  router.get(
    candidatesPath({ ...currentListParams(), add: "1" }),
    {},
    { preserveScroll: true, preserveState: true, only: ["show_add"] },
  )
}

function closeSheet() {
  const params = currentListParams()
  delete params.add
  router.get(
    candidatesPath(params),
    {},
    {
      preserveScroll: true,
      preserveState: true,
      only: ["show_add"],
    },
  )
}

/**
 * Empty mount whose only job is to publish the `handleViewSkipped`
 * closure on a globally-stable React ref so `showBulkResultToast`
 * (a non-component fn called from useEffect) can reach the
 * router-coupled navigate behaviour. This is the "ref-as-channel"
 * pattern — keeps the toast dispatcher pure while letting the
 * action button trigger an Inertia visit. Re-rendering this slot
 * is cheap (no DOM output).
 */
function ViewSkippedHandlerSlot({ onClick }: { onClick: () => void }) {
  useEffect(() => {
    viewSkippedHandlerRef.current = onClick
    return () => {
      if (viewSkippedHandlerRef.current === onClick) {
        viewSkippedHandlerRef.current = null
      }
    }
  }, [onClick])
  return null
}

const viewSkippedHandlerRef: { current: (() => void) | null } = {
  current: null,
}

/**
 * OKS-19 P4 fix #2 — discriminator for the bulk `cap_exceeded` flash.
 *
 * `FlashData.error` is `string | FlashErrorObject | undefined`. This
 * helper narrows safely: returns `true` iff the value is the
 * structured form AND `code === "too_many"`. Plain-string errors
 * (e.g. `empty_selection`) and `undefined` both return `false`.
 *
 * Exported for direct unit testing without a full Index.tsx render.
 */
export function isCapExceededError(
  err: string | FlashErrorObject | undefined,
): boolean {
  if (!err) return false
  if (typeof err === "string") return false
  return err.code === "too_many"
}

/**
 * Phase 4 P2 fix — build the URL for "clear bulk_skipped chip only".
 * Drops the `bulk_skipped` query param while preserving every other
 * URL parameter (status[], country, city, tags[], q, sort, page,
 * etc.). Returns the rebuilt path+query — caller passes to
 * `router.get`.
 *
 * Pure function over the search string; unit-testable without DOM.
 * `currentSearch` is window.location.search (e.g. `?bulk_skipped=42&status[]=reviewed`).
 *
 *   buildClearBulkSkippedUrl("?bulk_skipped=42&country=France")
 *   → "/candidates?country=France"
 *
 *   buildClearBulkSkippedUrl("?bulk_skipped=42")
 *   → "/candidates"          (no remaining params, no `?`)
 *
 *   buildClearBulkSkippedUrl("")
 *   → "/candidates"          (defensive — caller should never pass
 *                             this since the chip only renders when
 *                             bulk_skipped IS in the URL)
 */
export function buildClearBulkSkippedUrl(currentSearch: string): string {
  const params = new URLSearchParams(currentSearch)
  params.delete("bulk_skipped")
  const qs = params.toString()
  return `${candidatesPath()}${qs ? `?${qs}` : ""}`
}

/**
 * OKS-127 — Strip `?just_added=<id>` from the candidates URL while
 * preserving every other query-string key (filters, sort, page,
 * country, q, bulk_skipped, etc.). Wired into the `Index.tsx` mount
 * effect via `history.replaceState` so back-button / refresh /
 * shared-link copies do not re-trigger the row recency highlight.
 *
 * `currentSearch` is the raw `window.location.search` string
 * (including the leading `?` is OK — `URLSearchParams` handles it).
 *
 * Examples:
 *
 *   buildScrubbedJustAddedUrl("?just_added=42")
 *   → "/candidates"
 *
 *   buildScrubbedJustAddedUrl("?just_added=42&country=France&sort=name")
 *   → "/candidates?country=France&sort=name"
 *
 *   buildScrubbedJustAddedUrl("?country=France")
 *   → "/candidates?country=France"           (no marker present — no-op)
 *
 *   buildScrubbedJustAddedUrl("")
 *   → "/candidates"                           (defensive — caller
 *                                              should only invoke
 *                                              when just_added_id is
 *                                              non-null)
 */
export function buildScrubbedJustAddedUrl(currentSearch: string): string {
  const params = new URLSearchParams(currentSearch)
  params.delete("just_added")
  const qs = params.toString()
  return `${candidatesPath()}${qs ? `?${qs}` : ""}`
}

/**
 * Parse comma-separated ids out of `?bulk_skipped=42,91,12`. Exported
 * for direct unit testing — keeps the Index.tsx integration test
 * surface narrow.
 */
export function parseBulkSkipped(url: string): number[] {
  const queryStart = url.indexOf("?")
  if (queryStart === -1) return []
  const params = new URLSearchParams(url.slice(queryStart + 1))
  const raw = params.get("bulk_skipped")
  if (!raw) return []
  return raw
    .split(",")
    .map((s) => Number.parseInt(s.trim(), 10))
    .filter((n) => Number.isInteger(n) && n > 0)
}

/**
 * Extract `bulk_result` from `flash[:success]`. Discriminates the
 * legacy plain-string form (OKS-13 / OKS-18) from the OKS-19
 * structured object form. Returns `null` when not present so the
 * useEffect can early-return.
 */
export function extractBulkResult(
  flash: FlashData | undefined,
): BulkResult | null {
  if (!flash?.success) return null
  if (typeof flash.success === "string") return null
  const obj: FlashSuccessObject = flash.success
  return obj.bulk_result ?? null
}

export function bulkResultSignature(r: BulkResult): string {
  return `${r.event}:${r.transitioned_count}:${r.skipped_count}:${r.skipped_ids.join(",")}`
}

/**
 * Dispatch the sonner toast for a bulk-action redirect. Three
 * branches per UX Spec §Flows step 5:
 *   - skipped_count > 0 + transitioned_count > 0 → "X transitioned,
 *     Y skipped" + `View skipped` action button (testid
 *     `candidate-list-bulk-toast-skipped-link`).
 *   - skipped_count > 0 + transitioned_count === 0 → "Nothing
 *     transitioned — Y skipped" zero-key copy; same View-skipped
 *     action button.
 *   - skipped_count === 0 → simple "X transitioned" toast, NO
 *     action button.
 *
 * Duration: 6s (UX Spec — longer than the standard 3s success toast
 * so users have time to read the count and click View skipped).
 */
function showBulkResultToast(
  result: BulkResult,
  t: ReturnType<typeof useTranslation>["t"],
) {
  const { transitioned_count, skipped_count, skipped_ids } = result
  const hasSkipped = skipped_count > 0
  const allSkipped = transitioned_count === 0 && hasSkipped

  let summary: string
  if (allSkipped) {
    summary = t("candidates.list.bulk.toast_summary_zero", {
      count: 0,
      skipped: skipped_count,
    })
  } else if (hasSkipped) {
    summary = t("candidates.list.bulk.toast_summary_with_skipped", {
      count: transitioned_count,
      skipped: skipped_count,
    })
  } else {
    summary = t("candidates.list.bulk.toast_summary", {
      count: transitioned_count,
    })
  }

  // Sonner's `action` slot accepts either an Action object
  // ({label, onClick}) OR an arbitrary ReactNode. The Action variant
  // doesn't expose data-* passthrough, so we use a JSX node so the
  // locked testid can be applied directly to the rendered button.
  // Inline-styling matches sonner's default action-button look.
  const action =
    hasSkipped && skipped_ids.length > 0 ? (
      <button
        type="button"
        data-testid="candidate-list-bulk-toast-skipped-link"
        onClick={() => viewSkippedHandlerRef.current?.()}
        className="bg-foreground text-background ml-auto rounded px-2 py-1 text-xs font-medium"
      >
        {t("candidates.list.bulk.view_skipped")}
      </button>
    ) : undefined

  toast.success(summary, {
    duration: 6000,
    action,
  })
}

/**
 * shadcn `DropdownMenu` split-button.
 *
 * Two options: "Add one" and "Bulk CSV upload" (Link to imports/new).
 *
 * OKS-629 — the single-add item now NAVIGATES to `?add=1` (`openSheet`)
 * instead of flipping local dialog state. The add-candidate surface is a
 * URL-driven `Sheet` mounted once at page level (`AddCandidateSheet`), so
 * this menu no longer owns any open state or the OKS-617 menu→dialog
 * `finalFocus` handoff: the Base UI menu closes and restores focus to its
 * trigger *before* the async `?add=1` partial-nav mounts the Sheet, so the
 * synchronous menu→overlay focus race the handoff guarded against no
 * longer exists. OKS-657 — the Sheet captures no trigger of its own, so its
 * close-focus is pinned explicitly to THIS control (`add-candidate-trigger`)
 * via `finalFocusByTestId` at the `AddCandidateSheet` call site; focus lands
 * here on every close path rather than falling to `<body>`.
 */
function AddCandidateMenu() {
  const { t } = useTranslation()

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            type="button"
            data-testid="add-candidate-trigger"
            className="max-sm:h-11"
          >
            <Plus className="size-4" aria-hidden="true" />
            {t("candidates.add_menu.trigger")}
          </Button>
        }
      />
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          data-testid="add-candidate-menu-single"
          onClick={() => openSheet()}
        >
          <Plus className="size-4" aria-hidden="true" />
          {t("candidates.add_menu.single")}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          data-testid="bulk-import-link"
          render={
            <Link href={importCandidatesPath()}>
              <Upload className="size-4" aria-hidden="true" />
              {t("candidates.add_menu.bulk")}
            </Link>
          }
        />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

// Re-export ActiveFilterChips for test assertions.
export { ActiveFilterChips }
